/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { HeroAlphaMainStackFragment } from "@cosmos/hygraph-schema";

import { CosmosButtonProps, CosmosTextVariantEnum, LogoTitleDescButtonStack } from "@components/elements";
import { useIsFirefox } from "@components/hooks";
import { css, cva, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

const blurred = css({
	zIndex: "2",

	minHeight: "550px",
	mdDown: {
		minHeight: "initial",
	},

	"&.ff": {
		_after: {
			height: "500px",
			width: "500px",
			borderRadius: "500px",
			filter: "blur(300px) opacity(0.6)",
		},
	},

	_after: {
		content: "''",
		// Kept in step with `ANIMATION` (see src/constants/animation.ts). This is a Panda
		// css() literal and must stay statically extractable, so it cannot read the constant.
		animation: `fadein 1.2s ease-out 0.4s`,
		animationFillMode: "forwards",
		position: "absolute",
		zIndex: 1,
		opacity: 0,
		height: "400px",
		width: "400px",
		borderRadius: "400px",
		background: "#000",
		filter: "blur(140px)",

		mdDown: {
			content: "initial",
		},
	},
});

// Logo height is driven by the Hygraph `logoVariant` enum (CosmosEnumHeroAlphaLogoVariants).
// Keys are the literal enum values ("default" / "maxHeight64") rather than computed enum
// members so Panda can statically extract the recipe at build time. Each variant fully
// specifies its own max-height pair (desktop / smDown) so only one rule is ever applied —
// to add a future size, add one self-contained entry here keyed by its enum value.
const logoSize = cva({
	variants: {
		logoVariant: {
			default: {
				"& .logo img": {
					maxHeight: "32px",
					smDown: {
						maxHeight: "24px",
					},
				},
			},
			maxHeight64: {
				"& .logo img": {
					maxHeight: "64px",
					smDown: {
						maxHeight: "48px",
					},
				},
			},
		},
	},
	defaultVariants: {
		logoVariant: "default",
	},
});

const styles = css({
	h: "full",
	maxWidth: "600px",

	"& .cosmos-text.cosmos-title": {
		smDown: {
			textStyle: "headingXL",
		},
	},

	"& .cosmos-text.cosmos-desc": {
		lgDown: {
			textStyle: "paragraphMD",
		},
	},

	"& .logo-title-desc-button-stack": {
		position: "relative",
		zIndex: 2,
	},

	"& .button-list": {
		smDown: {
			flexWrap: "wrap",
			alignItems: "flex-start",
		},
	},
});

export interface HeroAlphaMainStackProps extends HeroAlphaMainStackFragment {
	isBlurred?: boolean;
}

export const HeroAlphaMainStack = ({
	isBlurred,
	buttons = [],
	modButtons = [],
	title,
	desc,
	logoImage,
	logoVariant,
	eyebrow,
	gradient,
}: HeroAlphaMainStackProps): React.JSX.Element | null => {
	//ff doesn't support blur the same as chrome, we need style tweaks
	const isFirefox = useIsFirefox();

	if (!title?.text && !desc && !buttons.length) {
		return null;
	}

	// Only pass a logoVariant the recipe actually defines. A value from a newer CMS schema
	// than this build knows about (e.g. a future "maxHeight96") would otherwise override
	// defaultVariants, match no recipe key, and emit no logo-size CSS at all — leaving the
	// logo at its intrinsic size. Coerce anything unrecognized to undefined → default.
	const knownLogoVariant =
		logoVariant && logoSize.variantMap.logoVariant.includes(logoVariant) ? logoVariant : undefined;

	const buttonSource = modButtons?.length ? modButtons : buttons;

	const cosmosButtons = buttonSource.map((button): CosmosButtonProps => {
		return {
			...button,
			size: "lg",
			analytics: {
				eventCategory: "button",
				interactionType: "click",
				eventAction: "navigation",
				eventLabel: button.name || button.label || "",
				eventValue: {
					component: "HeroAlpha",
					section: "hero",
				},
			},
		};
	});

	return (
		<VStack
			justifyContent="center"
			height="100%"
			alignItems="start"
			className={cx(
				`hero-alpha-main-stack`,
				isFirefox ? "ff" : "",
				isBlurred ? blurred : null,
				styles,
				logoSize({ logoVariant: knownLogoVariant }),
			)}
		>
			<LogoTitleDescButtonStack
				animate={true}
				stack={{ gap: isBlurred ? "eds.dimension.24" : "eds.dimension.32" }}
				image={logoImage}
				eyebrow={eyebrow}
				titleDescStack={{
					stack: { gap: "eds.dimension.24", align: "start" },
					title: {
						...title,
						as: "h1",
						variant: CosmosTextVariantEnum.heading2XL,
						color: "eds.text.contrast",
					},
					gradient,
					description: {
						html: desc,
						variant: CosmosTextVariantEnum.paragraphMD,
						as: "div",
						color: "eds.text.contrast",
					},
				}}
				buttons={cosmosButtons}
			/>
		</VStack>
	);
};
